import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Heading, 
  Grid, 
  GridItem, 
  Flex,
  Text,
  useBreakpointValue
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Market, MarketOutcome } from '../../types/marketTypes';
import { Event } from '../../types/types';
import { TradingMarketContent } from './TradingMarketContent';

interface TradingMarketSectionProps {
  title: string;
  markets: Market[];
  event?: Event;
  compact?: boolean;
  gridColumns?: string;
  /** Renders the heading as a toggle; off by default, so every other caller is unchanged. */
  collapsible?: boolean;
  defaultOpen?: boolean;
}

export const TradingMarketSection: React.FC<TradingMarketSectionProps> = ({
  title,
  markets,
  event,
  compact = false,
  gridColumns = "repeat(auto-fill, minmax(300px, 1fr))",
  collapsible = false,
  defaultOpen = true
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // Follows the section the caller wants open — the set now being played — without undoing a manual
  // toggle on every render: a price tick does not change `defaultOpen`, the start of a set does.
  const lastDefaultOpen = useRef(defaultOpen);
  useEffect(() => {
    if (lastDefaultOpen.current !== defaultOpen) {
      lastDefaultOpen.current = defaultOpen;
      setIsOpen(defaultOpen);
    }
  }, [defaultOpen]);

  // Responsive values
  const headingSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const responsiveGridColumns = useBreakpointValue({
    base: '1fr',
    sm: compact ? 'repeat(auto-fill, minmax(200px, 1fr))' : (gridColumns === '1fr' ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))'),
    md: compact ? 'repeat(auto-fill, minmax(180px, 1fr))' : gridColumns
  });
  
  if (!markets || markets.length === 0) {
    return null;
  }

  return (
    <Box mb={3}>
      {title && (
      <Flex
        as={collapsible ? 'button' : undefined}
        type={collapsible ? 'button' : undefined}
        onClick={collapsible ? () => setIsOpen(open => !open) : undefined}
        align="center"
        gap={1}
        mb={2}
        width={collapsible ? '100%' : undefined}
        textAlign="left"
      >
        {collapsible && (isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />)}
        <Heading size={headingSize}>{title}</Heading>
        {collapsible && !isOpen && (
          <Text fontSize="xs" color="gray.500">({markets.length})</Text>
        )}
      </Flex>
      )}
      {(!collapsible || isOpen) && (
      <Grid templateColumns={responsiveGridColumns} gap={2}>
        {markets.map((market) => (
          <GridItem 
            key={market.id} 
            p={2} 
            borderWidth="1px" 
            borderRadius="lg"
            bg={
              market.marketOutcome === MarketOutcome.VOIDED
                ? 'red.50'
                : market.marketOutcome === MarketOutcome.FINISHED
                ? 'green.50'
                : 'white'
            }
            position="relative"
          >
            <TradingMarketContent 
              market={market} 
              event={event}
              compact={compact}
            />
          </GridItem>
        ))}
      </Grid>
      )}
    </Box>
  );
};
